import { useCallback, useContext } from "react"

//SOAR Context
import { UserContext } from "../../../context/userContext"

//PrimeReact
import { Dialog } from "primereact/dialog"
import { ConfirmDialog } from "primereact/confirmdialog" // For <ConfirmDialog /> component
import { confirmDialog } from "primereact/confirmdialog" // For confirmDialog method

//Formik
import { Form, Formik } from "formik"
import * as Yup from "yup"

//Formik helpers
import FormikDropDown from "../../../helpers/formikInputs/FormikDropDown"
import FormikCalendar from "../../../helpers/formikInputs/FormikCalendar"
import FormikMultiSelectInput from "../../../helpers/formikInputs/FormikMultiSelectInput"
import FormikRichTextInput from "../../../helpers/formikInputs/FormikRichTextInput"
import { LogError } from "../../../helpers/logger"

//Housing note components
import {
  housingApiCreateNotePost,
  housingApiEditNotePost,
  housingApiRemoveNotePost,
} from "../housingApi"
import { useState } from "react"
import { useEffect } from "react"

export default function HousingNotesForm({
  showForm,
  noteProfile,
  onHideForm,
  onNoteCreated,
  participant,
  activeHouseId,
  houses,
  participants,
}) {
  const userCtx = useContext(UserContext)

  const getNewNote = useCallback(() => {
    const newNote = {
      noteType: "Participant",
      enterpriseId: userCtx.session.user.enterprise_id,
      organizationId: userCtx.session.user.organization_id,
      houseId: activeHouseId,
      UserId: userCtx.session.user.staff_id,
      participantIds: !!participant?.participant_id
        ? [participant?.participant_id]
        : [],
      note: "",
      noteDate: new Date(),
    }

    return newNote
  }, [
    activeHouseId,
    userCtx.session.user.enterprise_id,
    userCtx.session.user.organization_id,
    participant?.participant_id,
    userCtx.session.user.staff_id,
  ])

  const [participantHouses, setParticipantHouses] = useState(houses)
  const [houseParticipants, setHouseParticipants] = useState(participants)
  const [note, setNote] = useState(getNewNote())

  const validationSchema = Yup.object().shape({
    noteType: Yup.string(),
    note: Yup.string().required("Please enter a note"),
    houseId: Yup.string()
      .when("noteType", {
        is: "House",
        then: Yup.string().required("Please select a house"),
      })
      .nullable(),
    participantIds: Yup.array().when("noteType", {
      is: "Participant",
      then: Yup.array().min(1, "Please select a participant"),
    }),
    noteDate: Yup.date().required("Please enter the date for the note"),
  })

  const submitNote = async (values) => {
    try {
      if (values.id === undefined) {
        const result = await housingApiCreateNotePost(
          userCtx.tokenHousing,
          values
        )
        if (!!result) {
          userCtx.setNotificationInformation("Note created successfully")
          onNoteCreated(result)
        } else {
          LogError("Unable to save note", result)
          userCtx.setNotificationError(`Unable to save note: ${result.message}`)
        }
      } else {
        const result = await housingApiEditNotePost(
          userCtx.tokenHousing,
          values
        )
        if (!!result) {
          userCtx.setNotificationInformation("Note updated successfully")
          onNoteCreated(result, true)
        } else {
          LogError("Unable to save note", result)
          userCtx.setNotificationError(`Unable to save note: ${result.message}`)
        }
      }
    } catch (error) {
      userCtx.setNotificationError("Unable to save note")
      LogError("Unable to log Housing participant note", error)
      return false
    }
  }

  useEffect(() => {
    if (!!noteProfile) {
      if (!!noteProfile.participantId) {
        noteProfile.participantIds = [noteProfile.participantId]
        noteProfile.noteType = "Participant"
      }

      if (!!noteProfile.houseId && !noteProfile.participantId) {
        noteProfile.noteType = "House"
      }

      noteProfile.noteDate = noteProfile.date

      setNote(noteProfile)
    } else {
      setNote(getNewNote())
    }
  }, [getNewNote, noteProfile])

  const removeNote = async (id) => {
    try {
      const result = await housingApiRemoveNotePost(userCtx.tokenHousing, {
        id,
        userId: userCtx.session.user.staff_id,
      })

      if (!!result) {
        onHideForm(true)
      } else {
        LogError("Unable to save note", result)
        userCtx.setNotificationError(`Unable to save note: ${result.message}`)
      }
    } catch (error) {
      userCtx.setNotificationError("Unable to remove note")
      LogError("Unable to remove Housing participant note", error)
      return false
    }
  }

  return (
    <Dialog
      header={`New Housing note`}
      visible={showForm}
      onHide={() => {
        onHideForm()
      }}
      style={{ width: "800px", maxHeight: "900px" }}
    >
      <Formik
        initialValues={note}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={async (values) => {
          return await submitNote(values)
        }}
      >
        {({ isSubmitting, values, dirty, setFieldValue }) => (
          <Form>
            <ConfirmDialog />
            <FormikDropDown
              disabled={isSubmitting || values.id !== undefined}
              name={"noteType"}
              label={"Note Type"}
              options={[
                { label: "Participant Note", value: "Participant" },
                { label: "House Manager Log", value: "House" },
              ]}
              placeHolderValue="Please select a type"
              optionValue={"value"}
              optionLabel={"label"}
              onChangeCallback={(value) => {
                if (value === "House") {
                  setParticipantHouses(houses)
                  setFieldValue("participantIds", [])
                }
              }}
            />
            <FormikDropDown
              disabled={isSubmitting || values.id !== undefined}
              name={"houseId"}
              label={"House"}
              options={[
                { name: "No house", id: undefined },
                ...participantHouses,
              ]}
              placeHolderValue="No house selected"
              optionValue={"id"}
              optionLabel={"name"}
              showClear={true}
              required={values.noteType === "House"}
              onChangeCallback={(value) => {
                if (!!value && !!participants) {
                  const house = houses.find((house) => house.id === value)

                  setHouseParticipants(
                    participants.filter((participant) =>
                      house.activeParticipants.some(
                        (houseParticipant) =>
                          houseParticipant === participant.participant_id
                      )
                    )
                  )

                  if (values.participantIds.length > 0) {
                    const newParticipantIds = values.participantIds.filter(
                      (participant) =>
                        house.participants.some(
                          (houseParticipant) => houseParticipant === participant
                        )
                    )

                    setFieldValue("participantIds", newParticipantIds)
                  }
                } else {
                  setHouseParticipants(participants)
                }
              }}
            />
            {values.noteType === "Participant" ? (
              <FormikMultiSelectInput
                disabled={isSubmitting || values.id !== undefined}
                name={"participantIds"}
                label={"Participant"}
                options={houseParticipants}
                placeHolderValue="No participants selected"
                optionValue={"participant_id"}
                optionLabel={"name2"}
                required={values.noteType === "Participant"}
                onChangeCallBack={(value) => {
                  if (value.length > 0) {
                    setParticipantHouses(
                      houses.filter((house) =>
                        house.activeParticipants.some((participant) =>
                          value.includes(participant)
                        )
                      )
                    )
                  } else {
                    setParticipantHouses(houses)
                  }
                }}
              />
            ) : null}
            <FormikCalendar
              disabled={isSubmitting}
              name={"noteDate"}
              label={"Date and time"}
              maxDate={new Date()}
              showTime={true}
            />
            <FormikRichTextInput
              disabled={isSubmitting}
              name={"note"}
              label="Note"
              required={true}
              inputStyles={{ height: "250px", maxHeight: "80%" }}
            />
            <div style={{ float: "right" }}>
              <button
                type="submit"
                className="btn"
                disabled={isSubmitting || !dirty}
              >
                Save
              </button>
              {values.id !== undefined ? (
                <button
                  type="button"
                  className="btn"
                  style={{ marginLeft: "10px" }}
                  onClick={() => {
                    confirmDialog({
                      message:
                        "Are you sure you want to proceed with the deletion of this note?",
                      header: "Confirmation",
                      icon: "pi pi-exclamation-triangle",
                      accept: () => {
                        removeNote(values.id)
                      },
                    })
                  }}
                  disabled={isSubmitting}
                >
                  Delete
                </button>
              ) : null}

              <button
                type="button"
                className="btn"
                style={{ marginLeft: "10px" }}
                onClick={() => {
                  onHideForm()
                }}
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </Dialog>
  )
}
